import React, { useState, useRef, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  FaArrowRight,
  FaWifi,
  FaTabletAlt,
  FaMobile,
} from "react-icons/fa";

// Optimized intersection observer hook
const useIntersectionObserver = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setIsVisible(true);
          setHasAnimated(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -100px 0px",
      }
    );

    const currentElement = elementRef.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [hasAnimated]);

  return [elementRef, isVisible];
};

export const Streaming = () => {
  const { t } = useTranslation();

  // Use a single intersection observer for the whole section
  const [sectionRef, sectionInView] = useIntersectionObserver();

  // Optimized image URL - use a smaller, optimized image if possible
  const imageUrl = useMemo(() =>
    "https://wzd-cloud-standard-dffkd6cgbeg8e7a7.z03.azurefd.net/wz-2449-es-00003/2025-08-01-ContestanoVSLevante/wz-2449-es-00003_d688df33_livestream_thumbnail.png?st=2025-08-18T15%3A17%3A40Z&se=2026-07-22T15%3A17%3A40Z&sp=r&spr=https&sv=2025-05-05&sr=b&sig=%2BadR3lsexvh7IIfjPc3L9TsUA6qnIYJ/5qSXXYpJJV0%3D"
    , []);

  // Memoized stats data
  const statsData = useMemo(() => [
    {
      value: "4K",
      color: "blue",
      label: t("home.streaming.stats.quality.label"),
    },
    {
      value: "0ms",
      color: "purple",
      label: t("home.streaming.stats.latency.label"),
    },
    {
      value: "24/7",
      color: "green",
      label: t("home.streaming.stats.availability.label"),
    },
  ], [t]);

  // Memoized features data
  const featuresData = useMemo(() => [
    {
      icon: FaTabletAlt,
      title: t("home.streaming.features.feature1.title"),
      description: t("home.streaming.features.feature1.description"),
      color: "blue",
    },
    {
      icon: FaMobile,
      title: t("home.streaming.features.feature2.title"),
      description: t("home.streaming.features.feature2.description"),
      color: "purple",
    },
  ], [t]);

  // Optimized background style
  const backgroundStyle = useMemo(() => ({
    position: 'relative' as const,
    backgroundColor: '#f8fafc',
    backgroundImage: 'linear-gradient(135deg, #f1f5f9 0%, #ffffff 50%, #e0f2fe 100%)',
    overflow: 'hidden',
  }), []);

  return (
    <div

      //@ts-ignore
      ref={sectionRef}
      style={backgroundStyle}
    >
      {/* Simplified background elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-10 left-10 w-40 h-40 bg-blue-200 rounded-full blur-xl"></div>
        <div className="absolute bottom-10 right-10 w-48 h-48 bg-purple-200 rounded-full blur-xl"></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* Header Section */}
        <div className={`text-center mb-12 transition-all duration-300 ${sectionInView ? "opacity-100" : "opacity-0"}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
            <FaWifi className="w-3 h-3" />
            {t("home.streaming.liveBroadcast")}
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {t("home.streaming.mainHeading.line1")}
            <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              {t("home.streaming.mainHeading.line2")}
            </span>
          </h2>

          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {t("home.streaming.mainDescription")}
          </p>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-start">
          {/* Image Section */}
          <div className="relative">
            <div className="relative rounded-lg overflow-hidden shadow-lg">
              <img
                src={imageUrl}
                className="w-full h-auto"
                alt={t("home.streaming.imageAlt")}
                loading="lazy"
              />

            </div>

            {/* Live Tag */}
            <div className={`absolute top-3 right-3 transition-all duration-300 ${sectionInView ? "opacity-100" : "opacity-0"}`}>
              <div className="flex items-center gap-1 bg-white/90 backdrop-blur-sm rounded-full px-2 py-1 shadow-sm">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-medium text-gray-700">
                  {t("home.streaming.liveTag")}
                </span>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="space-y-6">
            {/* Feature Cards */}
            <div className="space-y-4">
              {featuresData.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <div
                    key={index}
                    className={`bg-white rounded-lg p-4 border border-gray-200 shadow-sm transition-all duration-300 hover:shadow-md ${sectionInView ? "opacity-100" : "opacity-0"
                      }`}
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 bg-${feature.color}-100 rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <IconComponent className={`text-${feature.color}-600 text-base`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 text-sm sm:text-base mb-1">
                          {feature.title}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* CTA Section */}
            {/* <div className={`bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg sm:rounded-xl p-4 sm:p-6 text-white transition-all duration-300 ${sectionInView ? "opacity-100" : "opacity-0"
              }`}>
              <h3 className="text-lg sm:text-xl font-bold mb-2">
                {t("home.streaming.cta.heading")}
              </h3>
              <p className="text-blue-100 text-sm sm:text-base mb-4 leading-relaxed">
                {t("home.streaming.cta.description")}
              </p>
              <a
                href="https://wizardfootball.com/preview/cBN@4$ZeQvnZj@5/98"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-blue-600 px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold text-sm sm:text-base hover:bg-gray-50 transition-colors duration-300 shadow"
              >
                <FaPlay className="text-sm" />
                {t("home.streaming.cta.button")}
                <FaArrowRight className="text-xs" />
              </a>
            </div> */}
          </div>
        </div>

        {/* Stats Section */}
        <div className={`mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 transition-all duration-300 ${sectionInView ? "opacity-100" : "opacity-0"}`}>
          {statsData.map((stat, index) => (
            <div
              key={index}
              className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-gray-100 text-center transition-all duration-300 hover:shadow-sm"
              style={{ transitionDelay: `${index * 50}ms` }}
            >
              <div className={`text-2xl sm:text-3xl font-bold text-${stat.color}-600 mb-1`}>
                {stat.value}
              </div>
              <div className="text-xs sm:text-sm text-gray-600 font-medium">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
